import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"

import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { Inactive } from "@/components/ui/inactive"
import { Skeleton } from "@/components/ui/skeleton"
import {
  ApiError,
  getNeighbours,
  getTransaction,
  getTransactionRaw,
  TRANSACTION_SORTS,
  type Neighbours,
  type NeighboursQuery,
  type NeighbourRow,
  type SortOrder,
  type TransactionCard,
  type TransactionPeriod,
  type TransactionSort,
} from "@/lib/api"
import { EMPTY, formatInt, formatScore, formatText } from "@/lib/format"
import { cn } from "@/lib/utils"

import { RawData } from "./raw-data"
import { Summary } from "./summary"

/**
 * Карточка транзакции `/transactions/:id` (§5 AMEND-10).
 *
 * Живые вкладки — Summary и Raw Data. Activity, Network analysis и Analyst log
 * стоят на своих местах и по клику открывают заглушку «не реализовано»: это
 * места образца, за которыми в этой сборке нет данных, и пустая вкладка
 * читалась бы как поломка.
 *
 * Слева — прокручиваемая колонка соседних транзакций той же выборки, что была на
 * списке. Фильтры приезжают из адресной строки: без них «предыдущая» в карточке
 * означала бы не ту строку, что стояла выше в таблице. Открытая напрямую (без
 * фильтров в URL) карточка берёт соседей из всей выборки — тогда 404 «вне
 * выборки» невозможен by construction.
 */

const TABS = [
  { key: "summary", label: "Summary" },
  { key: "activity", label: "Activity" },
  { key: "network", label: "Network analysis" },
  { key: "raw", label: "Raw Data" },
  { key: "log", label: "Analyst log" },
] as const
type TabKey = (typeof TABS)[number]["key"]

/** Заглушки трёх нереализованных вкладок — текст «не реализовано» своими словами. */
const STUBS: Record<string, string> = {
  activity: "Лента активности по транзакции не реализована в этой сборке.",
  network: "Сетевой анализ связей не реализован в этой сборке.",
  log: "Журнал действий аналитика не реализован в этой сборке.",
}

/** Кнопки шапки образца — место занято, действия за ними в этой сборке нет. */
const ACTIONS: ReadonlyArray<{ label: string; reason: string }> = [
  { label: "Mark as completed", reason: "Завершение проверки не реализовано в этой сборке." },
  { label: "Automations", reason: "Автоматизации не реализованы в этой сборке." },
  { label: "Assign", reason: "Назначение аналитика не реализовано в этой сборке." },
  { label: "Exclude customer", reason: "Исключение клиента не реализовано в этой сборке." },
  { label: "Refresh data", reason: "Обновление данных не реализовано в этой сборке." },
  { label: "Change", reason: "Изменение решения не реализовано в этой сборке." },
  { label: "Report", reason: "Выгрузка отчёта не реализована в этой сборке." },
]

/** Цвет точки соседа — те же три переменные палитры, что у исходов скоринга. */
const STATE_DOT: Record<string, string> = {
  APPROVE: "bg-approve",
  DECLINE: "bg-decline",
  REVIEW: "bg-review",
}

const PERIOD_VALUES = new Set<TransactionPeriod>(["1D", "7D", "30D", "3M", "12M", "custom"])
const SORT_VALUES = new Set<string>(TRANSACTION_SORTS)

/**
 * Фильтры соседей из адресной строки, пропущенные через белые списки: кривой
 * `?sort=` или `?period=` из URL иначе ушёл бы в ручку и вернул 422. Неизвестное
 * значение просто отбрасывается — соседи посчитаются по более широкой выборке,
 * а не сорвутся с ошибкой.
 */
function neighboursQuery(params: URLSearchParams): NeighboursQuery {
  const period = params.get("period")
  const sort = params.get("sort")
  const order = params.get("order")
  return {
    q: params.get("q") || undefined,
    state: params.get("state") || undefined,
    period: period && PERIOD_VALUES.has(period as TransactionPeriod)
      ? (period as TransactionPeriod)
      : undefined,
    from: params.get("from") || undefined,
    to: params.get("to") || undefined,
    sort: sort && SORT_VALUES.has(sort) ? (sort as TransactionSort) : undefined,
    order: order === "asc" || order === "desc" ? (order as SortOrder) : undefined,
  }
}

export function TransactionCardPage() {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const search = searchParams.toString()
  const suffix = search ? `?${search}` : ""

  const [card, setCard] = React.useState<TransactionCard | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [reload, setReload] = React.useState(0)

  const [neighbours, setNeighbours] = React.useState<Neighbours | null>(null)
  const [outOfSelection, setOutOfSelection] = React.useState(false)

  const [active, setActive] = React.useState<TabKey>("summary")

  const [raw, setRaw] = React.useState<Record<string, unknown> | null>(null)
  const [rawLoading, setRawLoading] = React.useState(false)
  const [rawError, setRawError] = React.useState<string | null>(null)
  // Счётчик попыток двигает только Retry: он и есть зависимость эффекта.
  const [rawAttempt, setRawAttempt] = React.useState(0)
  // «Уже запрашивали» держим в ref, а не в состоянии. Состояние, которое эффект
  // сам и выставляет, не должно стоять в его зависимостях: ре-ран дёргает
  // cleanup предыдущего прохода, и тот аборитит свой же незавершённый fetch.
  const rawRequested = React.useRef<string | null>(null)

  // Карточка.
  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    getTransaction(id, controller.signal)
      .then((result) => {
        setCard(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setCard(null)
        setError(cause instanceof Error ? cause.message : "Request failed")
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [id, reload])

  // Соседи. 404 — «вне текущей выборки»: сама карточка при этом показывается,
  // так что это отдельное тихое состояние колонки, а не ошибка страницы.
  React.useEffect(() => {
    const controller = new AbortController()
    setNeighbours(null)
    setOutOfSelection(false)
    getNeighbours(id, neighboursQuery(searchParams), controller.signal)
      .then((result) => setNeighbours(result))
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        if (cause instanceof ApiError && cause.status === 404) setOutOfSelection(true)
      })
    return () => controller.abort()
  }, [id, searchParams])

  // Raw грузится лениво — 641 колонка не нужна, пока вкладку не открыли.
  React.useEffect(() => {
    setRaw(null)
    setRawError(null)
  }, [id])

  React.useEffect(() => {
    if (active !== "raw") return
    // Ключ попытки: смена транзакции или Retry снимают гейт, переключение
    // вкладок туда-обратно — нет, данные уже в состоянии.
    const attempt = `${id}:${rawAttempt}`
    if (rawRequested.current === attempt) return
    rawRequested.current = attempt
    const controller = new AbortController()
    setRawLoading(true)
    getTransactionRaw(id, controller.signal)
      .then((result) => {
        setRaw(result)
        setRawError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setRaw(null)
        setRawError(cause instanceof Error ? cause.message : "Request failed")
      })
      .finally(() => {
        if (!controller.signal.aborted) setRawLoading(false)
      })
    return () => controller.abort()
  }, [active, rawAttempt, id])

  const title = formatText(card?.core?.transaction_id as string | null) || id

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {ACTIONS.map((action) => (
              <Inactive key={action.label} reason={action.reason} size="sm">
                {action.label}
              </Inactive>
            ))}
          </div>
        }
        tabs={<Tabs active={active} onChange={setActive} />}
      />

      <div className="flex flex-col gap-4 p-5 lg:flex-row">
        <NeighboursRail
          neighbours={neighbours}
          outOfSelection={outOfSelection}
          currentId={id}
          suffix={suffix}
          onGo={(target) => navigate(`/transactions/${target}${suffix}`)}
        />

        <div className="min-w-0 flex-1">
          <Link
            to={`/transactions${suffix}`}
            className="mb-3 inline-flex items-center gap-1.5 text-xs text-fg-muted transition-colors duration-150 ease-snap hover:text-fg"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            Back to transactions
          </Link>

          {loading && <BodySkeleton />}

          {!loading && error && (
            <section className="animate-panel-in rounded border border-line bg-surface p-8 text-center shadow-panel">
              <p className="text-sm font-medium text-decline">{error}</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-fg-muted">
                Карточка не загрузилась. Экран остаётся пустым намеренно — устаревших данных
                он не показывает.
              </p>
              <Button className="mt-4" onClick={() => setReload((value) => value + 1)}>
                Retry
              </Button>
            </section>
          )}

          {!loading && !error && card && (
            <>
              {active === "summary" && <Summary card={card} />}
              {active === "raw" && (
                <RawData
                  raw={raw}
                  loading={rawLoading}
                  error={rawError}
                  onRetry={() => {
                    setRawError(null)
                    setRawAttempt((n) => n + 1)
                  }}
                />
              )}
              {active !== "summary" && active !== "raw" && <Stub text={STUBS[active]} />}
            </>
          )}
        </div>
      </div>
    </>
  )
}

// ── вкладки ───────────────────────────────────────────────────────────────────

function Tabs({ active, onChange }: { active: TabKey; onChange: (key: TabKey) => void }) {
  return (
    <div className="-mb-px flex flex-wrap gap-1" role="tablist" aria-label="Transaction card tabs">
      {TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          onClick={() => onChange(tab.key)}
          className={cn(
            "border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 ease-snap",
            active === tab.key
              ? "border-accent text-fg"
              : "border-transparent text-fg-muted hover:text-fg",
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

/** Заглушка вкладки: занятое место образца, за которым нет данных. */
function Stub({ text }: { text: string }) {
  return (
    <section className="animate-panel-in rounded border border-dashed border-line-strong bg-surface-sunken p-10 text-center">
      <p className="stencil-label text-fg-muted">не реализовано</p>
      <p className="mx-auto mt-2 max-w-md text-xs text-fg-subtle">{text}</p>
    </section>
  )
}

// ── колонка соседей ───────────────────────────────────────────────────────────

function NeighboursRail({
  neighbours,
  outOfSelection,
  currentId,
  suffix,
  onGo,
}: {
  neighbours: Neighbours | null
  outOfSelection: boolean
  currentId: string
  suffix: string
  onGo: (id: string) => void
}) {
  const listRef = React.useRef<HTMLDivElement>(null)
  const currentRef = React.useRef<HTMLAnchorElement>(null)

  // Ставим текущую строку в центр прокрутки: ручка отдаёт окно вокруг неё, и без
  // подгонки колонка открывалась бы на самых верхних соседях, а не на открытой.
  React.useEffect(() => {
    const row = currentRef.current
    const box = listRef.current
    if (row && box) {
      box.scrollTop = row.offsetTop - box.clientHeight / 2 + row.clientHeight / 2
    }
  }, [neighbours])

  return (
    <aside className="w-full shrink-0 lg:w-64">
      <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
        <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
          <span className="stencil-label text-fg-subtle">
            {neighbours ? `${formatInt(neighbours.position)} / ${formatInt(neighbours.total)}` : EMPTY}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              aria-label="Previous transaction"
              disabled={!neighbours?.prev_id}
              onClick={() => neighbours?.prev_id && onGo(neighbours.prev_id)}
            >
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Next transaction"
              disabled={!neighbours?.next_id}
              onClick={() => neighbours?.next_id && onGo(neighbours.next_id)}
            >
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Button>
          </div>
        </header>

        <div ref={listRef} className="relative max-h-[calc(100vh-12rem)] overflow-y-auto">
          {!neighbours && !outOfSelection && <RailSkeleton />}

          {outOfSelection && (
            <p className="px-3 py-4 text-xs leading-relaxed text-fg-subtle">
              Транзакция открыта вне текущей выборки — соседей в этом срезе у неё нет.
            </p>
          )}

          {neighbours?.items.map((item) => (
            <NeighbourItem
              key={item.id}
              ref={item.id === currentId ? currentRef : undefined}
              item={item}
              current={item.id === currentId}
              suffix={suffix}
            />
          ))}
        </div>
      </section>
    </aside>
  )
}

const NeighbourItem = React.forwardRef<
  HTMLAnchorElement,
  { item: NeighbourRow; current: boolean; suffix: string }
>(({ item, current, suffix }, ref) => {
  const dot = STATE_DOT[(item.state ?? "").toUpperCase()] ?? "bg-fg-subtle"
  return (
    <Link
      ref={ref}
      to={`/transactions/${item.id}${suffix}`}
      aria-current={current ? "true" : undefined}
      className={cn(
        "flex items-center gap-2 border-b border-line px-3 py-2 transition-colors duration-100 ease-snap",
        current ? "border-l-2 border-l-accent bg-surface-sunken" : "hover:bg-accent-soft/60",
      )}
    >
      <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", dot)} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs text-fg">{formatText(item.user_fullname)}</span>
        <span className="block truncate text-xs text-fg-subtle">
          {formatText(item.transaction_id)}
        </span>
      </span>
      <span className="shrink-0 text-xs font-medium text-fg-muted">
        {formatScore(item.fraud_score)}
      </span>
    </Link>
  )
})
NeighbourItem.displayName = "NeighbourItem"

// ── скелетоны ─────────────────────────────────────────────────────────────────

function RailSkeleton() {
  return (
    <div className="space-y-px">
      {Array.from({ length: 12 }, (_, index) => (
        <div key={index} className="flex items-center gap-2 px-3 py-2">
          <Skeleton className="h-1.5 w-1.5 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-2 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

function BodySkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {Array.from({ length: 6 }, (_, index) => (
        <section key={index} className="rounded border border-line bg-surface p-4 shadow-panel">
          <Skeleton className="h-3 w-32" />
          <div className="mt-3 space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </section>
      ))}
    </div>
  )
}
